/**
 * CallNoteModal — Telephone Attendance & Telephone Message
 *
 * Opened via Ctrl+Shift+T from any authenticated CaseMinder route.
 * Renders as a right-hand slide-over (Sheet) with two tabs:
 *   1. Telephone Attendance — PDF filed, optional LawBillity
 *   2. Telephone Message — Email always sent, PDF only if case-related
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import {
  Phone, Clock, Loader2, Search, FileText, Mail, User, X, UserPlus, Building2, Activity, Pause, Play
} from "lucide-react";
import PolishNoteButton from "./PolishNoteButton";
import { ContactActivityPanel } from "./ContactActivityPanel";
import { STAFF } from "../config/staffConfig";
import axios from "axios";
import { API } from "../config/api";
import { attendanceTimeNarrative } from "../lib/attendanceNarrative";
import InvoiceLineField, { invoiceLine } from "./time/InvoiceLineField";
import { errorMessage } from "../utils/errorMessage";
import { staffNotifyCandidates } from "../lib/staffNotifyCandidates";

function nowLocal() {
  const d = new Date();
  return d.toISOString().slice(0, 16);
}

// SRA attendance-type taxonomy. Mirrors ATTENDANCE_TYPE_LABELS in
// backend/hub/call_notes.py — keep the two in sync.
const ATTENDANCE_TYPES = [
  ["telephone", "Telephone"],
  ["email", "Email"],
  ["letter", "Letter"],
  ["document", "Document review"],
  ["meeting", "Meeting"],
  ["research", "Research"],
  ["other", "Other"],
];
const ATTENDANCE_TYPE_LABELS = Object.fromEntries(ATTENDANCE_TYPES);

function formatClock(totalSeconds) {
  const t = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(t / 3600);
  const mm = String(Math.floor((t % 3600) / 60)).padStart(2, "0");
  const ss = String(t % 60).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default function CallNoteModal({ open, onClose, defaultMatterRef = "", initialTab = "attendance" }) {
  const [mode, setMode] = useState("attendance");
  const [saving, setSaving] = useState(false);

  // ── Common fields ──
  const [isCaseRelated, setIsCaseRelated] = useState(true);
  const [matterRef, setMatterRef] = useState(defaultMatterRef);
  const [matterDisplay, setMatterDisplay] = useState(defaultMatterRef);
  const [matterQuery, setMatterQuery] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [matterSearching, setMatterSearching] = useState(false);
  const [showMatterSearch, setShowMatterSearch] = useState(false);
  // Sangita's bug (May 2026): the amber "saved to database only" warning used
  // to fire the instant the toggle flipped ON, even before the user had any
  // chance to search for a matter. Track whether the user has actually
  // interacted with the matter search before showing the warning.
  const [matterSearchTouched, setMatterSearchTouched] = useState(false);
  const [partyName, setPartyName] = useState("");
  const [telephoneNumber, setTelephoneNumber] = useState("");
  const [callDateTime, setCallDateTime] = useState(nowLocal());

  // ── Attendance fields ──
  // attendance_type is the SRA work-type taxonomy. "telephone" preserves the
  // original phone-attendance behaviour (party + number required); other types
  // allow a bare note such as "Received mortgage offer and reviewed".
  const [attendanceType, setAttendanceType] = useState("telephone");
  const [direction, setDirection] = useState("inbound");
  const [channel, setChannel] = useState("telephone");
  const [noteText, setNoteText] = useState("");
  const [startTime, setStartTime] = useState(nowLocal());
  const [endTime, setEndTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [logToLawbillity, setLogToLawbillity] = useState(false);
  // BM-H4109: once the user flips the time switch themselves, a typed
  // duration no longer turns it on for them.
  const [timeSwitchTouched, setTimeSwitchTouched] = useState(false);
  // BM-H4124 (Vanessa, 9 Oct 2026): "can we have a stop start clock on
  // attendance notes?". The note's clock is an ordinary CaseMinder time clock
  // (/time-clocks, source "attendance_note"), so it keeps running when this
  // panel closes and shows in the pop-out clock. Saving the note closes it on
  // the server, so its time is logged once.
  const stopFilledRef = useRef("");  // BM-H4274: minutes Stop put in the duration box
  const [noteClock, setNoteClock] = useState(null);  // {id, status, elapsed_seconds, fetchedAt}
  const [clockBusy, setClockBusy] = useState(false);
  const [clockError, setClockError] = useState("");
  const [clockTick, setClockTick] = useState(0);
  const [notifyEmail, setNotifyEmail] = useState("");  // Retained for back-compat with any external consumers; not bound to UI any more (Brief 4, 28 May 2026)
  const [notifyRecipients, setNotifyRecipients] = useState([]);  // Brief 4 (28 May 2026): array of staff emails selected from STAFF picker
  const [notifyPickerOpen, setNotifyPickerOpen] = useState(false);
  const [notifyPickerQuery, setNotifyPickerQuery] = useState("");
  const [notifyEnabled, setNotifyEnabled] = useState(false);

  // Brief 72 — Attendance Note: on-behalf-of picker
  const [forAttendanceUserEmail, setForAttendanceUserEmail] = useState("");
  const [forAttendanceUserDisplay, setForAttendanceUserDisplay] = useState("");
  const [showAttendanceStaffPicker, setShowAttendanceStaffPicker] = useState(false);
  const [attendanceStaffQuery, setAttendanceStaffQuery] = useState("");
  const [attendanceStaffResults, setAttendanceStaffResults] = useState([]);
  const [attendanceStaffSearching, setAttendanceStaffSearching] = useState(false);
  const attendanceStaffTimerRef = useRef(null);

  // Brief 4 (28 May 2026): notify multi-select source list — staffConfig
  // is the source of truth for who can be notified. Only is_active staff
  // are shown. Already-selected recipients are excluded from the dropdown
  // so they can't be re-added.
  const activeStaff = useMemo(() => STAFF.filter((s) => s.is_active), []);
  // BM-H589: shared rule, no cap (this picker used to stop at 20).
  const filteredNotifyCandidates = useMemo(
    () => staffNotifyCandidates(notifyPickerQuery, notifyRecipients),
    [notifyPickerQuery, notifyRecipients],
  );

  // Brief 72 — Tel Message Lawbillity fields
  const [telMsgLogToLawbillity, setTelMsgLogToLawbillity] = useState(false);
  const [telMsgDurationMinutes, setTelMsgDurationMinutes] = useState(6);

  // ── Message fields ──
  const [forUserEmail, setForUserEmail] = useState("");
  const [forUserDisplay, setForUserDisplay] = useState("");
  const [staffQuery, setStaffQuery] = useState("");
  const [staffResults, setStaffResults] = useState([]);
  const [staffSearching, setStaffSearching] = useState(false);
  const [showStaffSearch, setShowStaffSearch] = useState(false);
  const [contactDetails, setContactDetails] = useState("");
  const [messageText, setMessageText] = useState("");

  // ── Contact fields ──
  const [contactId, setContactId] = useState(null);
  const [contactDisplay, setContactDisplay] = useState("");
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [contactSearching, setContactSearching] = useState(false);
  const [showSaveContact, setShowSaveContact] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [newContact, setNewContact] = useState({
    type: "client", name: "", organisation: "", phone: "", email: "",
  });

  const matterTimerRef = useRef(null);
  const staffTimerRef = useRef(null);
  const contactTimerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Reset form when opened
  useEffect(() => {
    if (open) {
      setMode(initialTab);
      // BM-H4137 (9 Oct 2026): always start on "Relates to a matter". Opened
      // away from a matter page it used to start OFF, so notes saved with no
      // matter, were never filed, and the toast said only "saved".
      setIsCaseRelated(true);
      setMatterRef(defaultMatterRef);
      setMatterDisplay(defaultMatterRef);
      setMatterSearchTouched(false);
      setCallDateTime(nowLocal());
      setStartTime(nowLocal());
      setEndTime("");
      setDurationMinutes("");
      setNoteText("");
      setMessageText("");
      setPartyName("");
      setTelephoneNumber("");
      setForUserEmail("");
      setForUserDisplay("");
      setContactDetails("");
      setLogToLawbillity(false);
      setTimeSwitchTouched(false);
      setForAttendanceUserEmail("");
      setForAttendanceUserDisplay("");
      setShowAttendanceStaffPicker(false);
      setAttendanceStaffQuery("");
      setAttendanceStaffResults([]);
      setNotifyEnabled(false);
      setNotifyEmail("");
      setNotifyRecipients([]);
      setNotifyPickerOpen(false);
      setNotifyPickerQuery("");
      setAttendanceType("telephone");
      setDirection("inbound");
      setChannel("telephone");
      setContactId(null);
      setContactDisplay("");
      setContactQuery("");
      setContactResults([]);
      setShowSaveContact(false);
      setNewContact({ type: "client", name: "", organisation: "", phone: "", email: "" });
      setTelMsgLogToLawbillity(false);
      setTelMsgDurationMinutes(6);
      setNarrativeEdit(null);
      setTelMsgNarrativeEdit(null);
    }
  }, [open, defaultMatterRef]);

  // time-recording-foundations (ticket 2): narrative generation now calls
  // the same AI-assisted endpoint the Bonsai mail add-in uses
  // (POST /time-entries/ai-narrative -> hub.time_narrative.generate_narrative)
  // instead of a plain client-side string template. The template string is
  // kept as the fallback for when the request itself fails (network error,
  // offline) so the field is never left blank -- never a fallback for "the
  // AI declined", which the backend already handles by returning its own
  // canned narrative in that case.
  // BM-H4126 (Vanessa, 9 Oct 2026): the invoice line is the standard line
  // for the kind of note and the person, never the note's content or a
  // model's summary of it. The server builds the same line and saves that.
  // Vanessa, 28 Oct 2026: that line is the default and the writer may edit
  // it or ask Haiku to suggest one (InvoiceLineField). null = not edited.
  const [narrativeEdit, setNarrativeEdit] = useState(null);
  const [telMsgNarrativeEdit, setTelMsgNarrativeEdit] = useState(null);
  const standardLine = isCaseRelated ? attendanceTimeNarrative(attendanceType, channel, partyName) : "";
  const telMsgStandardLine = isCaseRelated ? attendanceTimeNarrative("telephone", "telephone", partyName) : "";
  const invoiceNarrative = isCaseRelated ? invoiceLine(narrativeEdit, standardLine) : "";
  const telMsgNarrative = isCaseRelated ? invoiceLine(telMsgNarrativeEdit, telMsgStandardLine) : "";

  // ── Matter search (debounced) ──
  const searchMatters = useCallback(async (q) => {
    if (q.length < 2) { setMatterResults([]); return; }
    setMatterSearching(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
      const resp = await axios.get(`${API}/bonsai/filing/matters`, {
        params: { q, limit: 8 },
        headers: userId ? { "X-Bonsai-User-Id": userId } : {},
        withCredentials: true,
      });
      setMatterResults(resp.data || []);
    } catch { setMatterResults([]); }
    setMatterSearching(false);
  }, []);

  const handleMatterQueryChange = useCallback((e) => {
    const q = e.target.value;
    setMatterQuery(q);
    // Once the user has typed anything, the search is "touched" — the warning
    // becomes a meaningful signal ("you cleared the field, no matter linked").
    // Until first keystroke we suppress the warning so we don't spook users
    // who just turned the toggle on and are about to search.
    if (q.length > 0 && !matterSearchTouched) setMatterSearchTouched(true);
    if (matterTimerRef.current) clearTimeout(matterTimerRef.current);
    matterTimerRef.current = setTimeout(() => searchMatters(q), 300);
  }, [searchMatters, matterSearchTouched]);

  const selectMatter = useCallback((m) => {
    const ref = m.reference || m.matter_reference || m.matter_title || "";
    const title = m.matter_title || m.title || m.client_name || ref;
    setMatterRef(ref);
    setMatterDisplay(title ? `${ref} — ${title}` : ref);
    setMatterResults([]);
    setMatterQuery("");
    setShowMatterSearch(false);
  }, []);

  // ── Staff search ──
  const searchStaff = useCallback(async (q) => {
    if (q.length < 1) { setStaffResults([]); return; }
    setStaffSearching(true);
    try {
      const resp = await axios.get(`${API}/staff`, { withCredentials: true });
      const all = resp.data?.fee_earners || [];
      const matches = all
        .filter((d) =>
          (d.name || "").toLowerCase().includes(q.toLowerCase()) ||
          (d.email || "").toLowerCase().includes(q.toLowerCase())
        )
        .map((d) => ({ name: d.name, email: d.email, initials: d.initials }))
        .slice(0, 8);
      setStaffResults(matches);
    } catch { setStaffResults([]); }
    setStaffSearching(false);
  }, []);

  const handleStaffQueryChange = useCallback((e) => {
    const q = e.target.value;
    setStaffQuery(q);
    if (staffTimerRef.current) clearTimeout(staffTimerRef.current);
    staffTimerRef.current = setTimeout(() => searchStaff(q), 200);
  }, [searchStaff]);

  const selectStaff = useCallback((s) => {
    setForUserEmail(s.email);
    setForUserDisplay(s.name);
    setStaffResults([]);
    setStaffQuery("");
    setShowStaffSearch(false);
  }, []);

  // ── Contact search (debounced) ──
  const searchContacts = useCallback(async (q) => {
    if (q.length < 2) { setContactResults([]); return; }
    setContactSearching(true);
    try {
      const params = { q, limit: 8 };
      if (isCaseRelated && matterRef) params.matter_ref = matterRef;
      const resp = await axios.get(`${API}/contacts/search`, { params, withCredentials: true });
      setContactResults(resp.data?.results || []);
    } catch { setContactResults([]); }
    setContactSearching(false);
  }, [isCaseRelated, matterRef]);

  const handleContactQueryChange = useCallback((e) => {
    const q = e.target.value;
    setContactQuery(q);
    if (contactTimerRef.current) clearTimeout(contactTimerRef.current);
    contactTimerRef.current = setTimeout(() => searchContacts(q), 300);
  }, [searchContacts]);

  const selectContact = useCallback((c) => {
    setContactId(c.id);
    setContactDisplay(c.display_name);
    setContactResults([]);
    setContactQuery("");
    // Pre-fill phone from contact if empty
    if (c.primary_phone && !telephoneNumber) {
      setTelephoneNumber(c.primary_phone);
    }
    // Pre-fill party name from contact display name if empty
    if (c.display_name && !partyName) {
      setPartyName(c.display_name);
    }
    setShowSaveContact(false);
  }, [telephoneNumber, partyName]);

  // ── Save as contact ──
  const CONTACT_TYPE_MAP = {
    client: { label: "client", role: "client" },
    estate_agent: { label: "estate_agent", role: "agent" },
    mortgage_broker: { label: "mortgage_broker", role: "broker" },
    accountant: { label: "accountant", role: "accountant" },
    law_firm: { label: "law_firm", role: "law_firm" },
    other_party: { label: "other_party", role: "other_party" },
    other: { label: "other", role: "other" },
  };

  const saveAsContact = useCallback(async () => {
    if (!newContact.name.trim()) { toast.error("Contact name is required"); return; }
    setSavingContact(true);
    try {
      const mapping = CONTACT_TYPE_MAP[newContact.type] || CONTACT_TYPE_MAP.other;
      const names = newContact.name.trim().split(/\s+/);
      const resp = await axios.post(`${API}/contacts`, {
        type: newContact.type === "law_firm" ? "organisation" : "person",
        display_name: newContact.name.trim() + (newContact.organisation ? ` (${newContact.organisation.trim()})` : ""),
        first_name: names[0] || "",
        last_name: names.slice(1).join(" ") || "",
        organisation: newContact.organisation.trim(),
        primary_email: newContact.email.trim(),
        primary_phone: newContact.phone.trim(),
        labels: [mapping.label],
      }, { withCredentials: true });

      if (resp.data?.status === "ok") {
        const created = resp.data.contact;
        // Link to matter if applicable
        if (isCaseRelated && matterRef && created.id) {
          await axios.post(`${API}/contacts/link-matter`, {
            contact_id: created.id,
            matter_ref: matterRef,
            role: mapping.role,
          }, { withCredentials: true }).catch(() => {});
        }
        // Populate the contact field
        setContactId(created.id);
        setContactDisplay(created.display_name);
        if (created.primary_phone && !telephoneNumber) setTelephoneNumber(created.primary_phone);
        if (!partyName) setPartyName(created.display_name);
        setShowSaveContact(false);
        setNewContact({ type: "client", name: "", organisation: "", phone: "", email: "" });
        toast.success("Contact saved");
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save contact"));
    }
    setSavingContact(false);
  }, [newContact, isCaseRelated, matterRef, telephoneNumber, partyName]);

  // ── Inline validation (Brief 84) ──
  // Replaces the old silent toast.error early-returns. Each entry carries the
  // field's data-testid so the inline summary can scroll/focus the offending
  // input when the (disabled) Save button area is clicked.
  const clockSeconds = useMemo(() => {
    if (!noteClock) return 0;
    const extra = noteClock.status === "running" ? (Date.now() - noteClock.fetchedAt) / 1000 : 0;
    return Math.max(0, (noteClock.elapsed_seconds || 0) + extra);
    // clockTick re-reads Date.now() once a second while the clock runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteClock, clockTick]);
  const clockMins = clockSeconds > 0 ? Math.max(1, Math.ceil(clockSeconds / 60)) : 0;

  const computeDurMins = useCallback(() => {
    let durMins = parseInt(durationMinutes) || 0;
    if (!durMins && startTime && endTime) {
      const s = new Date(startTime);
      const e = new Date(endTime);
      durMins = Math.max(1, Math.round((e - s) / 60000));
    }
    // BM-H4124: a typed duration wins; otherwise the clock's time.
    if (!durMins && noteClock) durMins = clockMins;
    return durMins;
  }, [durationMinutes, startTime, endTime, noteClock, clockMins]);

  useEffect(() => {
    if (!noteClock || noteClock.status !== "running") return undefined;
    const t = setInterval(() => setClockTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [noteClock]);

  // A clock started from an earlier note on this matter is picked up again,
  // so closing the panel mid-call loses nothing.
  useEffect(() => {
    setNoteClock(null);
    setClockError("");
    if (!open || !isCaseRelated || !matterRef) return undefined;
    let live = true;
    (async () => {
      try {
        const resp = await axios.get(`${API}/time-clocks`, { withCredentials: true });
        const mine = (resp?.data?.clocks || []).find(
          (c) => c.source === "attendance_note" && c.matter_ref === matterRef);
        if (live && mine) setNoteClock({ ...mine, fetchedAt: Date.now() });
      } catch {
        // No clock to pick up; Start clock still works.
      }
    })();
    return () => { live = false; };
  }, [open, isCaseRelated, matterRef]);

  const clockAction = useCallback(async (action) => {
    setClockBusy(true);
    setClockError("");
    try {
      let resp;
      if (action === "start") {
        resp = await axios.post(`${API}/time-clocks/start`, {
          matter_ref: matterRef,
          narrative: invoiceNarrative.trim() || "Attendance note",
          source: "attendance_note",
        }, { withCredentials: true });
      } else {
        const url = action === "pause"
          ? `${API}/time-clocks/${noteClock.id}/pause`
          : `${API}/time-clocks/${noteClock.id}/resume`;
        resp = await axios.post(url, {}, { withCredentials: true });
      }
      const next = { ...resp.data, fetchedAt: Date.now() };
      if (action === "pause") {
        // BM-H4274: a pause leaves the duration box alone. Filling it here
        // froze the minutes, and a typed duration wins on save, so time after
        // a resume was lost. Stop is what fills the box.
        next.elapsed_seconds = resp.data.accumulated_seconds || 0;
      } else if (action === "resume") {
        next.elapsed_seconds = resp.data.accumulated_seconds || 0;
        // Resumed after Stop: drop the minutes Stop put in the box (unless
        // they were edited since) so the clock's full time counts again.
        setDurationMinutes((cur) => (stopFilledRef.current && cur === stopFilledRef.current ? "" : cur));
        stopFilledRef.current = "";
      } else {
        next.elapsed_seconds = 0;
      }
      setNoteClock(next);
    } catch (e) {
      // e.g. 409: no time on a matter until the client has agreed the CCL.
      setClockError(errorMessage(e, "The clock could not be started"));
    }
    setClockBusy(false);
  }, [matterRef, invoiceNarrative, noteClock]);

  // BM-H4274: Stop pauses the clock if it is running, then puts its minutes
  // in the duration box (still editable). The save closes the clock.
  const stopClock = useCallback(async () => {
    if (!noteClock) return;
    let secs = noteClock.elapsed_seconds || 0;
    if (noteClock.status === "running") {
      setClockBusy(true);
      setClockError("");
      try {
        const resp = await axios.post(`${API}/time-clocks/${noteClock.id}/pause`, {}, { withCredentials: true });
        secs = resp.data.accumulated_seconds || 0;
        setNoteClock({ ...resp.data, elapsed_seconds: secs, fetchedAt: Date.now() });
      } catch (e) {
        setClockError(errorMessage(e, "The clock could not be stopped"));
        setClockBusy(false);
        return;
      }
      setClockBusy(false);
    }
    const mins = secs > 0 ? Math.max(1, Math.ceil(secs / 60)) : 0;
    if (mins) {
      stopFilledRef.current = String(mins);
      setDurationMinutes(String(mins));
    }
  }, [noteClock]);

  // BM-H4109 (Vanessa, 9 Oct 2026): a duration typed on a matter note is
  // time to log. Her 08:12 note showed time on screen and stored 0 because
  // the switch was off by default. Typing a duration now turns it on, unless
  // the user has set the switch themselves.
  useEffect(() => {
    if (!timeSwitchTouched && isCaseRelated && computeDurMins() > 0) setLogToLawbillity(true);
  }, [timeSwitchTouched, isCaseRelated, computeDurMins]);

  const timeLogHint = useMemo(() => {
    const mins = computeDurMins();
    if (!isCaseRelated) return mins > 0 ? "Time is only logged against a matter. Turn on 'Relates to a matter' to log it." : "";
    if (!mins) return "No duration entered, so no time will be logged.";
    if (!logToLawbillity) return `Time logging is off, so these ${mins} minutes will not be logged.`;
    return `${mins} minutes will be logged to the matter.`;
  }, [computeDurMins, isCaseRelated, logToLawbillity]);

  const attendanceValidationErrors = useMemo(() => {
    const errs = [];
    // party + telephone are only required for a telephone attendance. Other
    // types (document review, email, ...) accept a bare note with no party.
    if (attendanceType === "telephone") {
      if (!partyName.trim()) errs.push({ field: "party-name", testid: "party-name-input", message: "Who the call is with — required" });
      if (!telephoneNumber.trim()) errs.push({ field: "telephone", testid: "telephone-input", message: "Telephone number — required" });
    }
    if (!noteText.trim()) errs.push({ field: "note-text", testid: "note-text-input", message: "Attendance note content — required" });
    if (isCaseRelated && !matterRef) errs.push({ field: "matter", testid: "matter-search-input", message: "Select a matter or uncheck 'Relates to a matter'" });
    if (notifyEnabled && notifyRecipients.length === 0) errs.push({ field: "notify", testid: "notify-picker-input", message: "Select at least one recipient to notify" });
    // Duration is only required when the note will actually be logged to
    // LawBillity for billing — a plain attendance record for the file needs
    // no time. Vanessa's 11 Aug 2026 silent-save-block (BM-H137) traced to
    // the old unconditional rule blocking notes that had no billing side.
    if (logToLawbillity && isCaseRelated && !computeDurMins()) {
      errs.push({ field: "duration", testid: "duration-input", message: "Provide a duration or start/end time to record time" });
    }
    return errs;
  }, [attendanceType, partyName, telephoneNumber, noteText, isCaseRelated, matterRef, notifyEnabled, notifyRecipients, logToLawbillity, computeDurMins]);

  const messageValidationErrors = useMemo(() => {
    const errs = [];
    if (!partyName.trim()) errs.push({ field: "party-name", testid: "party-name-input", message: "Caller name — required" });
    if (!telephoneNumber.trim()) errs.push({ field: "telephone", testid: "telephone-input", message: "Telephone number — required" });
    if (!forUserEmail) errs.push({ field: "for-user", testid: "staff-search-input", message: "Select who the message is for" });
    if (!messageText.trim()) errs.push({ field: "message-text", testid: "message-text-input", message: "Message text — required" });
    if (isCaseRelated && !matterRef) errs.push({ field: "matter", testid: "matter-search-input", message: "Select a matter or uncheck 'Relates to a matter'" });
    return errs;
  }, [partyName, telephoneNumber, forUserEmail, messageText, isCaseRelated, matterRef]);

  const focusField = useCallback((testid) => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-testid="${testid}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") el.focus({ preventScroll: true });
    }
  }, []);

  const scrollToFirstInvalid = useCallback((errors) => {
    if (errors && errors.length > 0) focusField(errors[0].testid);
  }, [focusField]);

  // ── Save: Attendance ──
  const saveAttendance = useCallback(async () => {
    // State is guaranteed valid here (Save is disabled while errors exist),
    // but guard defensively without a toast so an unexpected invalid click is
    // a no-op rather than a malformed request.
    if (attendanceValidationErrors.length > 0) return;

    const durMins = computeDurMins();

    setSaving(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
      const userEmail = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
      const resp = await axios.post(`${API}/attendance-notes`, {
        matter_ref: isCaseRelated ? matterRef : "",
        is_case_related: isCaseRelated,
        contact_id: contactId || null,
        attendance_type: attendanceType,
        party_name: partyName.trim(),
        telephone_number: telephoneNumber.trim(),
        direction,
        channel,
        for_user_email: forAttendanceUserEmail || userEmail,
        note_text: noteText.trim(),
        call_started_at: new Date(startTime).toISOString(),
        call_ended_at: endTime ? new Date(endTime).toISOString() : "",
        duration_minutes: durMins,
        log_time_to_lawbillity: logToLawbillity && isCaseRelated,
        clock_id: (isCaseRelated && noteClock?.id) || "",
        notify_email: notifyEnabled && notifyRecipients.length === 0 ? notifyEmail.trim() : "",  // Legacy single-recipient back-compat (Brief 4)
        notify_emails: notifyEnabled ? notifyRecipients : [],
        invoice_narrative: isCaseRelated ? invoiceNarrative.trim() : "",
      }, { withCredentials: true });

      if (resp.data?.status === "ok") {
        const d = resp.data;
        // Build a per-sub-path diagnostic summary
        const lines = [];
        if (d.sharepoint_ok === true) lines.push("✓ Filed to Attendances");
        else if (d.sharepoint_ok === false) lines.push(`✗ SharePoint: ${d.sharepoint_reason || "failed"}`);
        if (d.lawbillity_ok === true) lines.push("✓ Time recorded on the matter in CaseMinder");
        else if (d.lawbillity_ok === false) lines.push(`✗ Time not recorded: ${d.lawbillity_reason || "failed"}`);
        else if (isCaseRelated && !d.time_entry_id) lines.push("No time logged on this note");  // BM-H4109: say so, never silence
        if (d.clock_note) lines.push(d.clock_note);  // BM-H4124
        setNoteClock(null);
        if (d.notify_ok === true) lines.push(`✓ Notified ${d.notification_sent_to}`);
        else if (d.notify_ok === false) lines.push(`✗ Notify: ${d.notify_reason || "failed"}`);
        if (d.contact_update) lines.push(`${d.contact_update.startsWith("Could not") ? "✗" : "✓"} ${d.contact_update}`);  // BM-H4109

        const anyFailed = d.sharepoint_ok === false || d.lawbillity_ok === false || d.notify_ok === false;
        if (!isCaseRelated) lines.push("Saved, but not filed to any matter: it is not in a matter's Attendances or FileSnap.");  // BM-H4137
        const msg = lines.length ? lines.join("\n") : "Attendance note saved.";
        if (anyFailed) {
          toast.error(msg, { duration: 10000 });
        } else {
          toast.success(msg, { duration: 6000 });
        }
        onClose();
      } else {
        toast.error(resp.data?.detail || "Failed to save attendance note");
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save"));
    }
    setSaving(false);
  }, [attendanceValidationErrors, computeDurMins, attendanceType, partyName, telephoneNumber, noteText, isCaseRelated, matterRef, startTime, endTime, direction, channel, logToLawbillity, notifyEnabled, notifyEmail, notifyRecipients, contactId, invoiceNarrative, noteClock, onClose]);

  // — Attendance staff picker (Brief 72) —
  const searchAttendanceStaff = async (q) => {
    if (q.length < 1) { setAttendanceStaffResults([]); return; }
    setAttendanceStaffSearching(true);
    try {
      const resp = await axios.get(`${API}/staff`, { withCredentials: true });
      const all = resp.data?.fee_earners || [];
      const matches = all
        .filter((d) =>
          (d.name || "").toLowerCase().includes(q.toLowerCase()) ||
          (d.email || "").toLowerCase().includes(q.toLowerCase())
        )
        .map((d) => ({ name: d.name, email: d.email, initials: d.initials }))
        .slice(0, 8);
      setAttendanceStaffResults(matches);
    } catch { setAttendanceStaffResults([]); }
    setAttendanceStaffSearching(false);
  };

  const handleAttendanceStaffQueryChange = (e) => {
    const q = e.target.value;
    setAttendanceStaffQuery(q);
    if (attendanceStaffTimerRef.current) clearTimeout(attendanceStaffTimerRef.current);
    attendanceStaffTimerRef.current = setTimeout(() => searchAttendanceStaff(q), 200);
  };

  const selectAttendanceStaff = (s) => {
    setForAttendanceUserEmail(s.email);
    setForAttendanceUserDisplay(s.name);
    setAttendanceStaffResults([]);
    setAttendanceStaffQuery("");
    setShowAttendanceStaffPicker(false);
  };

  // ── Save: Message ──
  const saveMessage = useCallback(async () => {
    if (messageValidationErrors.length > 0) return;

    const takenByEmail = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "system@bonsai.law";

    setSaving(true);
    try {
      const resp = await axios.post(`${API}/telephone-messages`, {
        matter_ref: isCaseRelated ? matterRef : "",
        is_case_related: isCaseRelated,
        contact_id: contactId || null,
        party_name: partyName.trim(),
        telephone_number: telephoneNumber.trim(),
        for_user_email: forUserEmail,
        taken_by_email: takenByEmail,
        message_text: messageText.trim(),
        contact_details: contactDetails.trim(),
        call_time: new Date(callDateTime).toISOString(),
        // Brief 72 — Lawbillity fields
        log_time_to_lawbillity: telMsgLogToLawbillity && isCaseRelated && !!matterRef,
        duration_minutes: telMsgDurationMinutes,
        invoice_narrative: isCaseRelated ? telMsgNarrative.trim() : "",
      }, { withCredentials: true });

      if (resp.data?.status === "ok") {
        const d = resp.data;
        const lines = [`✓ Telephone message emailed to ${forUserDisplay || forUserEmail}.`];
        if (!isCaseRelated) lines.push("Not filed to any matter.");  // BM-H4137
        if (d.lawbillity_ok === true) lines.push("✓ Time recorded on the matter in CaseMinder");
        else if (d.lawbillity_ok === false) lines.push(`✗ Time not recorded: ${d.lawbillity_reason || "failed"}`);
        if (d.contact_update) lines.push(`${d.contact_update.startsWith("Could not") ? "✗" : "✓"} ${d.contact_update}`);  // BM-H4109
        const anyFailed = d.lawbillity_ok === false;
        const msg = lines.join("\n");
        if (anyFailed) {
          toast.error(msg, { duration: 10000 });
        } else {
          toast.success(msg, { duration: 6000 });
        }
        onClose();
      } else {
        toast.error(resp.data?.detail || "Failed to send telephone message");
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save"));
    }
    setSaving(false);
  }, [messageValidationErrors, partyName, telephoneNumber, forUserEmail, forUserDisplay, messageText, contactDetails, isCaseRelated, matterRef, callDateTime, telMsgLogToLawbillity, telMsgDurationMinutes, telMsgNarrative, onClose]);

  return (
  <>
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-navy-card border-l border-navy-border p-0 flex flex-col overflow-hidden" data-testid="call-note-modal">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-navy-border">
          <SheetTitle className="text-white flex items-center gap-2">
            <Phone className="w-5 h-5 text-teal" />
            Attendance Note
          </SheetTitle>
        </SheetHeader>

        <Tabs value={mode} onValueChange={setMode} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-5 mt-3 grid grid-cols-2 bg-navy border border-navy-border" data-testid="call-note-tabs">
            <TabsTrigger value="attendance" className="text-xs data-[state=active]:bg-teal data-[state=active]:text-white" data-testid="tab-attendance">
              <FileText className="w-3.5 h-3.5 mr-1.5" />Attendance
            </TabsTrigger>
            <TabsTrigger value="message" className="text-xs data-[state=active]:bg-blue-700 data-[state=active]:text-white" data-testid="tab-message">
              <Mail className="w-3.5 h-3.5 mr-1.5" />Telephone Message
            </TabsTrigger>
          </TabsList>

          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-5 pb-5">
            {/* ═══ COMMON FIELDS ═══ */}
            <div className="space-y-4 mt-4">
              {/* Relates to matter */}
              <div className="flex items-center justify-between" data-testid="case-related-toggle">
                <Label className="text-sm text-slate-300">Relates to a matter</Label>
                <Switch checked={isCaseRelated} onCheckedChange={(v) => { setIsCaseRelated(v); if (!v) { setMatterRef(""); setMatterDisplay(""); } setMatterSearchTouched(false); }} data-testid="case-related-switch" />
              </div>

              {/* Matter search */}
              {isCaseRelated && (
                <div data-testid="matter-field">
                  <Label className="text-xs text-slate-400 mb-1.5 block">Matter</Label>
                  {matterDisplay ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-teal/40 bg-teal-dim/10">
                      <span className="text-sm text-teal flex-1 truncate" data-testid="matter-selected">{matterDisplay}</span>
                      <button onClick={() => { setMatterRef(""); setMatterDisplay(""); setShowMatterSearch(true); setMatterSearchTouched(true); }} className="text-slate-500 hover:text-slate-300 p-0.5">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                      <Input value={matterQuery} onChange={handleMatterQueryChange} placeholder="Search matters..."
                        className="pl-8 bg-navy border-navy-border text-white text-sm" autoFocus data-testid="matter-search-input" />
                      {matterSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-400 animate-spin" />}
                      {matterResults.length > 0 && (
                        <div className="absolute z-20 top-full mt-1 left-0 right-0 border border-navy-border rounded-md bg-navy-card max-h-40 overflow-y-auto shadow-xl" data-testid="matter-dropdown">
                          {matterResults.map((m, i) => {
                            const name = m.matter_title || m.title || m.reference || "";
                            const ref = m.reference || "";
                            return (
                              <div key={i} onClick={() => selectMatter(m)}
                                className="px-3 py-2 hover:bg-teal-dim/30 cursor-pointer border-b border-navy-border/50 last:border-0"
                                data-testid={`matter-option-${i}`}>
                                <div className="text-xs text-white font-medium truncate">{name}</div>
                                {ref && ref !== name && <div className="text-[10px] text-slate-500">{ref}</div>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Sangita's bug fix: only show this warning once the user has actively engaged with the matter search and left it blank — not on the initial toggle-ON state. */}
                      {matterSearchTouched && !matterQuery && <p className="text-[10px] text-amber-400/70 mt-1" data-testid="matter-blank-warning">Note will be saved to database only, not filed to SharePoint</p>}
                    </div>
                  )}
                </div>
              )}
              {!isCaseRelated && (
                <p className="text-[10px] text-amber-400/70 -mt-2">Note will be saved to database only, not filed to SharePoint</p>
              )}

              {/* Party name */}
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">{mode === "attendance" ? "Who the attendance is with" : "Caller name"}{(mode === "message" || attendanceType === "telephone") ? " *" : ""}</Label>
                <Input value={partyName} onChange={e => setPartyName(e.target.value)} placeholder="e.g. Mr Jose Hernandez"
                  className="bg-navy border-navy-border text-white text-sm" data-testid="party-name-input" />
              </div>

              {/* Contact search */}
              <div data-testid="contact-field">
                <Label className="text-xs text-slate-400 mb-1.5 block">Contact</Label>
                {contactDisplay ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-teal/40 bg-teal-dim/10">
                    <User className="w-3.5 h-3.5 text-teal" />
                    <span className="text-sm text-teal flex-1 truncate" data-testid="contact-selected">{contactDisplay}</span>
                    <button onClick={() => setShowActivityPanel(true)} className="text-teal hover:text-teal p-0.5" title="View activity" data-testid="contact-activity-btn">
                      <Activity className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => { setContactId(null); setContactDisplay(""); }} className="text-slate-500 hover:text-slate-300 p-0.5">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                    <Input value={contactQuery} onChange={handleContactQueryChange} placeholder="Search contacts..."
                      className="pl-8 bg-navy border-navy-border text-white text-sm" data-testid="contact-search-input" />
                    {contactSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-teal animate-spin" />}
                    {contactResults.length > 0 && (
                      <div className="absolute z-20 top-full mt-1 left-0 right-0 border border-navy-border rounded-md bg-navy-card max-h-40 overflow-y-auto shadow-xl" data-testid="contact-dropdown">
                        {contactResults.map((c, i) => (
                          <div key={i} onClick={() => selectContact(c)}
                            className="px-3 py-2 hover:bg-teal-dim/30 cursor-pointer border-b border-navy-border/50 last:border-0"
                            data-testid={`contact-option-${i}`}>
                            <div className="text-xs text-white font-medium truncate">{c.display_name}</div>
                            <div className="text-[10px] text-slate-500 truncate">
                              {[c.primary_phone, c.primary_email].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {contactQuery.length >= 2 && !contactSearching && contactResults.length === 0 && (
                      <div className="absolute z-20 top-full mt-1 left-0 right-0 border border-navy-border rounded-md bg-navy-card p-2 shadow-xl">
                        <p className="text-[10px] text-slate-500 mb-1.5">No contacts found</p>
                        <button onClick={() => {
                          setShowSaveContact(true);
                          setNewContact(prev => ({ ...prev, name: contactQuery, phone: telephoneNumber }));
                          setContactResults([]);
                        }}
                          className="flex items-center gap-1.5 text-xs text-teal hover:text-teal font-medium"
                          data-testid="save-as-contact-btn">
                          <UserPlus className="w-3.5 h-3.5" /> Save as contact
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Save as contact inline form */}
              {showSaveContact && (
                <div className="border border-teal/40 rounded-lg bg-teal-dim/10 p-3 space-y-3" data-testid="save-contact-form">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-teal">New Contact</span>
                    <button onClick={() => setShowSaveContact(false)} className="text-slate-500 hover:text-slate-300">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {/* Contact type */}
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block">Type *</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        ["client", "Client"],
                        ["estate_agent", "Estate Agent"],
                        ["mortgage_broker", "Mortgage Broker"],
                        ["accountant", "Accountant"],
                        ["law_firm", "Law Firm"],
                        ["other_party", "Other Party"],
                        ["other", "Other"],
                      ].map(([val, label]) => (
                        <button key={val} onClick={() => setNewContact(p => ({ ...p, type: val }))}
                          className={`px-2 py-1 text-[10px] rounded-md border transition-colors ${
                            newContact.type === val
                              ? "bg-teal text-white border-teal"
                              : "bg-navy text-slate-400 border-navy-border hover:border-slate-500"
                          }`}
                          data-testid={`contact-type-${val}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Name *</Label>
                      <Input value={newContact.name} onChange={e => setNewContact(p => ({ ...p, name: e.target.value }))}
                        className="bg-navy border-navy-border text-white text-xs h-8" data-testid="new-contact-name" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Organisation</Label>
                      <Input value={newContact.organisation} onChange={e => setNewContact(p => ({ ...p, organisation: e.target.value }))}
                        className="bg-navy border-navy-border text-white text-xs h-8" data-testid="new-contact-org" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Phone</Label>
                      <Input value={newContact.phone} onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))}
                        className="bg-navy border-navy-border text-white text-xs h-8" data-testid="new-contact-phone" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500 mb-1 block">Email</Label>
                      <Input value={newContact.email} onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))}
                        className="bg-navy border-navy-border text-white text-xs h-8" data-testid="new-contact-email" />
                    </div>
                  </div>
                  <Button onClick={saveAsContact} disabled={savingContact} size="sm"
                    className="w-full bg-teal hover:bg-teal text-white h-8 text-xs"
                    data-testid="confirm-save-contact-btn">
                    {savingContact ? <><Loader2 className="w-3 h-3 animate-spin mr-1.5" />Saving...</> : <><UserPlus className="w-3 h-3 mr-1.5" />Save Contact</>}
                  </Button>
                </div>
              )}

              {/* Telephone number */}
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">Telephone number{(mode === "message" || attendanceType === "telephone") ? " *" : ""}</Label>
                <Input value={telephoneNumber} onChange={e => setTelephoneNumber(e.target.value)} placeholder="01234 567890"
                  className="bg-navy border-navy-border text-white text-sm" data-testid="telephone-input" />
              </div>

              {/* Date/time */}
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">{mode === "attendance" ? "Date/time" : "Date/time of call"} *</Label>
                <Input type="datetime-local" value={callDateTime} onChange={e => { setCallDateTime(e.target.value); setStartTime(e.target.value); }}
                  className="bg-navy border-navy-border text-white text-sm" data-testid="call-datetime-input" />
              </div>
            </div>

            {/* ═══ ATTENDANCE TAB ═══ */}
            <TabsContent value="attendance" className="mt-0 pt-0 space-y-4 border-0">
              <div className="border-t border-navy-border/50 pt-4 mt-4 space-y-4">
                {/* Attendance type — any billable attendance, not just a call */}
                <div>
                  <Label className="text-xs text-slate-400 mb-1.5 block">Attendance type *</Label>
                  <Select value={attendanceType} onValueChange={setAttendanceType}>
                    <SelectTrigger className="bg-navy border-navy-border text-white text-sm" data-testid="attendance-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-card border-navy-border">
                      {ATTENDANCE_TYPES.map(([val, label]) => (
                        <SelectItem key={val} value={val} className="text-white hover:bg-teal-dim/30" data-testid={`attendance-type-${val}`}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {attendanceType !== "telephone" && (
                    <p className="text-[10px] text-slate-500 mt-1">Party name and telephone number are optional for this type.</p>
                  )}
                </div>

                {/* Direction + Channel are telephone-call specifics only */}
                {attendanceType === "telephone" && (
                  <>
                    {/* Direction */}
                    <div>
                      <Label className="text-xs text-slate-400 mb-1.5 block">Direction *</Label>
                      <div className="grid grid-cols-2 gap-2" data-testid="direction-selector">
                        <button onClick={() => setDirection("inbound")}
                          className={`py-2 px-3 text-xs font-medium rounded-md border transition-colors ${direction === "inbound" ? "bg-teal text-white border-teal" : "bg-navy text-slate-400 border-navy-border hover:border-slate-500"}`}
                          data-testid="direction-inbound">Inbound</button>
                        <button onClick={() => setDirection("outbound")}
                          className={`py-2 px-3 text-xs font-medium rounded-md border transition-colors ${direction === "outbound" ? "bg-teal text-white border-teal" : "bg-navy text-slate-400 border-navy-border hover:border-slate-500"}`}
                          data-testid="direction-outbound">Outbound</button>
                      </div>
                    </div>

                    {/* Channel */}
                    <div>
                      <Label className="text-xs text-slate-400 mb-1.5 block">Channel *</Label>
                      <Select value={channel} onValueChange={setChannel}>
                        <SelectTrigger className="bg-navy border-navy-border text-white text-sm" data-testid="channel-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-navy-card border-navy-border">
                          <SelectItem value="telephone" className="text-white hover:bg-teal-dim/30">Telephone</SelectItem>
                          <SelectItem value="teams" className="text-white hover:bg-teal-dim/30">Teams</SelectItem>
                          <SelectItem value="zoom" className="text-white hover:bg-teal-dim/30">Zoom</SelectItem>
                          <SelectItem value="in_person" className="text-white hover:bg-teal-dim/30">In-person</SelectItem>
                          <SelectItem value="other" className="text-white hover:bg-teal-dim/30">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {/* Note body */}
                <div>
                  <Label className="text-xs text-slate-400 mb-1.5 block">Attendance note *</Label>
                  <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={6}
                    placeholder="Type attendance content..."
                    className="bg-navy border-navy-border text-white text-sm resize-none" data-testid="note-text-input" />
                </div>

                {/* Start / End / Duration */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block">Start time</Label>
                    <Input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)}
                      className="bg-navy border-navy-border text-white text-[11px] h-8" data-testid="start-time-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block">End time</Label>
                    <Input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)}
                      className="bg-navy border-navy-border text-white text-[11px] h-8" data-testid="end-time-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500 mb-1 block">Duration <span className="text-slate-600">(only needed to log time)</span></Label>
                    <Input type="number" value={durationMinutes} onChange={e => setDurationMinutes(e.target.value)} placeholder="mins"
                      className="bg-navy border-navy-border text-white text-[11px] h-8" min={1} data-testid="duration-input" />
                  </div>
                </div>
                {/* BM-H4124: start/stop clock. It fills the duration box, which stays editable. */}
                <div className="flex items-center gap-2 -mt-1" data-testid="note-clock">
                  {!noteClock ? (
                    <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]"
                      disabled={clockBusy || !isCaseRelated || !matterRef}
                      onClick={() => clockAction("start")} data-testid="note-clock-start">
                      <Clock className="w-3 h-3 mr-1" />Start clock
                    </Button>
                  ) : (
                    <>
                      <span className="font-mono text-sm text-white" data-testid="note-clock-elapsed">{formatClock(clockSeconds)}</span>
                      {noteClock.status === "running" ? (
                        <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" disabled={clockBusy}
                          onClick={() => clockAction("pause")} data-testid="note-clock-pause">
                          <Pause className="w-3 h-3 mr-1" />Pause</Button>
                      ) : (
                        <>
                          <span className="text-[10px] text-amber-400" data-testid="note-clock-paused">Paused</span>
                          <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" disabled={clockBusy}
                            onClick={() => clockAction("resume")} data-testid="note-clock-resume">
                            <Play className="w-3 h-3 mr-1" />Resume</Button>
                        </>
                      )}
                      <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" disabled={clockBusy}
                        onClick={stopClock} title="Stop timing and put the minutes in the duration box"
                        data-testid="note-clock-stop">Stop</Button>
                    </>
                  )}
                  {(!isCaseRelated || !matterRef) && !noteClock && (
                    <span className="text-[10px] text-slate-500">The clock needs a matter</span>
                  )}
                </div>
                {clockError && <p className="text-[11px] text-amber-400 -mt-2" data-testid="note-clock-error">{clockError}</p>}
                {timeLogHint && (
                  <p className={`text-[11px] -mt-2 ${/will be logged to/.test(timeLogHint) ? "text-slate-400" : "text-amber-400"}`} data-testid="time-log-hint">{timeLogHint}</p>
                )}

                {/* Brief 72 — On-behalf-of picker */}
                <div data-testid="attendance-for-user-field">
                  <Label className="text-xs text-slate-400 mb-1.5 block">This note is for</Label>
                  {forAttendanceUserDisplay ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-indigo-700/40 bg-indigo-900/10">
                      <User className="w-3.5 h-3.5 text-indigo-400" />
                      <span className="text-sm text-indigo-300 flex-1" data-testid="attendance-staff-selected">{forAttendanceUserDisplay}</span>
                      <button onClick={() => { setForAttendanceUserEmail(""); setForAttendanceUserDisplay(""); setShowAttendanceStaffPicker(true); }} className="text-slate-500 hover:text-slate-300 p-0.5">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <button
                        onClick={() => setShowAttendanceStaffPicker((v) => !v)}
                        className="text-xs text-indigo-400 hover:text-indigo-200 underline"
                        data-testid="attendance-for-change-btn"
                      >
                        Me (change)
                      </button>
                      {showAttendanceStaffPicker && (
                        <div className="relative mt-1.5">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                          <Input value={attendanceStaffQuery} onChange={handleAttendanceStaffQueryChange} placeholder="Search staff..."
                            className="pl-8 bg-navy border-navy-border text-white text-sm" autoFocus data-testid="attendance-staff-search-input" />
                          {attendanceStaffSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-indigo-400 animate-spin" />}
                          {attendanceStaffResults.length > 0 && (
                            <div className="absolute z-20 top-full mt-1 left-0 right-0 border border-navy-border rounded-md bg-navy-card max-h-40 overflow-y-auto shadow-xl" data-testid="attendance-staff-dropdown">
                              {attendanceStaffResults.map((s, i) => (
                                <div key={i} onClick={() => selectAttendanceStaff(s)}
                                  className="px-3 py-2 hover:bg-indigo-900/30 cursor-pointer border-b border-navy-border/50 last:border-0 flex items-center gap-2"
                                  data-testid={`attendance-staff-option-${i}`}>
                                  <div className="w-6 h-6 rounded-full bg-indigo-800/50 flex items-center justify-center text-[9px] text-indigo-300 font-bold">{s.initials || "?"}</div>
                                  <div>
                                    <div className="text-xs text-white font-medium">{s.name}</div>
                                    <div className="text-[10px] text-slate-500">{s.email}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* LawBillity toggle */}
                {isCaseRelated && (
                  <div className="flex items-center justify-between py-2 px-3 rounded-md border border-indigo-800/30 bg-indigo-900/10" data-testid="lawbillity-toggle">
                    <div>
                      <Label className="text-sm text-indigo-300">Record time on the matter</Label>
                      <p className="text-[10px] text-slate-500 mt-0.5">Records the duration above as time on this matter</p>
                    </div>
                    <Switch checked={logToLawbillity} onCheckedChange={(v) => { setTimeSwitchTouched(true); setLogToLawbillity(v); }} data-testid="lawbillity-switch" />
                  </div>
                )}

                {/* Invoice narrative */}
                {isCaseRelated && (
                  <InvoiceLineField
                    value={narrativeEdit}
                    onChange={setNarrativeEdit}
                    standardLine={standardLine}
                    content={noteText}
                    testId="invoice-narrative"
                  />
                )}

                {/* Email notification toggle */}
                {/* Brief 72: clarification note */}
                <p className="text-[10px] text-slate-400 -mb-1">Notifications go to the people you select below; time will be logged against the fee earner above.</p>
                <div className="flex items-center justify-between py-2 px-3 rounded-md border border-amber-800/30 bg-status-amber-dim/10" data-testid="notify-email-toggle">
                  <div className="flex-1 min-w-0">
                    <Label className="text-sm text-status-amber">Email notification</Label>
                    <p className="text-[10px] text-slate-500 mt-0.5">Send a copy of this note by email</p>
                  </div>
                  <Switch
                    checked={notifyEnabled}
                    onCheckedChange={(v) => {
                      setNotifyEnabled(v);
                      if (!v) {
                        setNotifyRecipients([]);
                        setNotifyPickerOpen(false);
                        setNotifyPickerQuery("");
                      }
                    }}
                    data-testid="notify-email-switch"
                  />
                </div>
                {notifyEnabled && (
                  <div data-testid="notify-multi-select">
                    {/* Selected chips */}
                    {notifyRecipients.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="notify-chips">
                        {notifyRecipients.map((email) => {
                          const member = activeStaff.find((s) => s.email === email);
                          const label = member ? member.full_name : email;
                          return (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-teal/20 text-teal border border-teal/40"
                              data-testid={`notify-chip-${email}`}
                            >
                              {label}
                              <button
                                type="button"
                                onClick={() => setNotifyRecipients((prev) => prev.filter((e) => e !== email))}
                                className="hover:text-white"
                                aria-label={`Remove ${label}`}
                                data-testid={`notify-chip-remove-${email}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {/* Search box */}
                    <Input
                      type="text"
                      value={notifyPickerQuery}
                      onChange={(e) => { setNotifyPickerQuery(e.target.value); setNotifyPickerOpen(true); }}
                      onFocus={() => setNotifyPickerOpen(true)}
                      placeholder="Search staff to notify (name, initials or email)..."
                      className="bg-navy border-navy-border text-white text-sm"
                      data-testid="notify-picker-input"
                    />
                    {/* Dropdown */}
                    {notifyPickerOpen && filteredNotifyCandidates.length > 0 && (
                      <ul
                        className="mt-1 max-h-44 overflow-auto rounded-md border border-navy-border bg-navy"
                        data-testid="notify-picker-dropdown"
                      >
                        {filteredNotifyCandidates.map((s) => (
                          <li key={s.email}>
                            <button
                              type="button"
                              onClick={() => {
                                setNotifyRecipients((prev) => prev.includes(s.email) ? prev : [...prev, s.email]);
                                setNotifyPickerQuery("");
                                setNotifyPickerOpen(false);
                              }}
                              className="w-full text-left px-3 py-1.5 hover:bg-navy-border/60 text-white text-sm"
                              data-testid={`notify-picker-option-${s.email}`}
                            >
                              <div className="font-medium">{s.full_name}</div>
                              <div className="text-[10px] text-slate-400">{s.email}</div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Save Attendance */}
              {attendanceValidationErrors.length > 0 && (
                <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2" role="alert" data-testid="attendance-validation-summary">
                  <p className="text-xs font-semibold text-amber-300 mb-1">Cannot save yet:</p>
                  <ul className="space-y-0.5">
                    {attendanceValidationErrors.map((err) => (
                      <li key={err.field} className="text-[11px] text-amber-200/90 flex items-start gap-1.5">
                        <span className="text-amber-400 leading-4">•</span>
                        <button type="button" onClick={() => focusField(err.testid)}
                          className="text-left hover:underline"
                          data-testid={`attendance-error-${err.field}`}>
                          {err.message}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center gap-2 mt-4">
                <div className="flex-1"
                  onClick={() => {
                    if (attendanceValidationErrors.length > 0) {
                      scrollToFirstInvalid(attendanceValidationErrors);
                      const n = attendanceValidationErrors.length;
                      toast.error(`Cannot save yet — ${n} field${n === 1 ? "" : "s"} need attention.`, { duration: 4000 });
                    }
                  }}
                  data-testid="save-attendance-wrap">
                  <Button onClick={saveAttendance} disabled={saving || attendanceValidationErrors.length > 0}
                    title={attendanceValidationErrors.length > 0 ? `${attendanceValidationErrors.length} field${attendanceValidationErrors.length === 1 ? "" : "s"} need attention before saving` : undefined}
                    className={`w-full bg-teal hover:bg-teal text-white h-10${attendanceValidationErrors.length > 0 ? " pointer-events-none" : ""}`}
                    data-testid="save-attendance-btn">
                    {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : <><FileText className="w-4 h-4 mr-2" />Save Attendance Note</>}
                  </Button>
                </div>
                <PolishNoteButton
                  rawNote={noteText}
                  onPolished={(polished) => setNoteText(polished)}
                  matterRef={isCaseRelated ? matterRef : null}
                  clientName={contactDisplay || null}
                  disabled={saving}
                />
              </div>
            </TabsContent>

            {/* ═══ MESSAGE TAB ═══ */}
            <TabsContent value="message" className="mt-0 pt-0 space-y-4 border-0">
              <div className="border-t border-navy-border/50 pt-4 mt-4 space-y-4">
                {/* Message for */}
                <div>
                  <Label className="text-xs text-slate-400 mb-1.5 block">Message for *</Label>
                  {forUserDisplay ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-blue-700/40 bg-blue-900/10">
                      <User className="w-3.5 h-3.5 text-blue-400" />
                      <span className="text-sm text-blue-300 flex-1" data-testid="staff-selected">{forUserDisplay}</span>
                      <button onClick={() => { setForUserEmail(""); setForUserDisplay(""); setShowStaffSearch(true); }} className="text-slate-500 hover:text-slate-300 p-0.5">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                      <Input value={staffQuery} onChange={handleStaffQueryChange} placeholder="Search staff..."
                        className="pl-8 bg-navy border-navy-border text-white text-sm" data-testid="staff-search-input" />
                      {staffSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-400 animate-spin" />}
                      {staffResults.length > 0 && (
                        <div className="absolute z-20 top-full mt-1 left-0 right-0 border border-navy-border rounded-md bg-navy-card max-h-40 overflow-y-auto shadow-xl" data-testid="staff-dropdown">
                          {staffResults.map((s, i) => (
                            <div key={i} onClick={() => selectStaff(s)}
                              className="px-3 py-2 hover:bg-blue-900/30 cursor-pointer border-b border-navy-border/50 last:border-0 flex items-center gap-2"
                              data-testid={`staff-option-${i}`}>
                              <div className="w-6 h-6 rounded-full bg-blue-800/50 flex items-center justify-center text-[9px] text-blue-300 font-bold">{s.initials || "?"}</div>
                              <div>
                                <div className="text-xs text-white font-medium">{s.name}</div>
                                <div className="text-[10px] text-slate-500">{s.email}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Contact details */}
                <div>
                  <Label className="text-xs text-slate-400 mb-1.5 block">Contact details (optional)</Label>
                  <Input value={contactDetails} onChange={e => setContactDetails(e.target.value)} placeholder="e.g. Best time to call back after 3pm"
                    className="bg-navy border-navy-border text-white text-sm" data-testid="contact-details-input" />
                </div>

                {/* Message text */}
                <div>
                  <Label className="text-xs text-slate-400 mb-1.5 block">Message *</Label>
                  <Textarea value={messageText} onChange={e => setMessageText(e.target.value)} rows={5}
                    placeholder="Type the message..."
                    className="bg-navy border-navy-border text-white text-sm resize-none" data-testid="message-text-input" />
                </div>
              </div>

              {/* Brief 72: Duration, LawBillity, Narrative for Tel Message */}
              {isCaseRelated && (
                <div>
                  <Label className="text-[10px] text-slate-500 mb-1 block">Duration (minutes)</Label>
                  <Input type="number" value={telMsgDurationMinutes} onChange={e => setTelMsgDurationMinutes(parseInt(e.target.value) || 6)} placeholder="6"
                    className="bg-navy border-navy-border text-white text-sm" min={1} data-testid="tel-msg-duration-input" />
                </div>
              )}
              {isCaseRelated && matterRef && (
                <div className="flex items-center justify-between py-2 px-3 rounded-md border border-indigo-800/30 bg-indigo-900/10" data-testid="tel-msg-lawbillity-toggle">
                  <div>
                    <Label className="text-sm text-indigo-300">Record time on the matter</Label>
                    <p className="text-[10px] text-slate-500 mt-0.5">Records {telMsgDurationMinutes} minutes for this matter</p>
                  </div>
                  <Switch checked={telMsgLogToLawbillity} onCheckedChange={setTelMsgLogToLawbillity} data-testid="tel-msg-lawbillity-switch" />
                </div>
              )}
              {isCaseRelated && (
                <InvoiceLineField
                  value={telMsgNarrativeEdit}
                  onChange={setTelMsgNarrativeEdit}
                  standardLine={telMsgStandardLine}
                  content={messageText}
                  testId="tel-msg-narrative"
                />
              )}

              {/* Save Message */}
              {messageValidationErrors.length > 0 && (
                <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2" role="alert" data-testid="message-validation-summary">
                  <p className="text-xs font-semibold text-amber-300 mb-1">Cannot save yet:</p>
                  <ul className="space-y-0.5">
                    {messageValidationErrors.map((err) => (
                      <li key={err.field} className="text-[11px] text-amber-200/90 flex items-start gap-1.5">
                        <span className="text-amber-400 leading-4">•</span>
                        <button type="button" onClick={() => focusField(err.testid)}
                          className="text-left hover:underline"
                          data-testid={`message-error-${err.field}`}>
                          {err.message}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-4"
                onClick={() => {
                  if (messageValidationErrors.length > 0) {
                    scrollToFirstInvalid(messageValidationErrors);
                    const n = messageValidationErrors.length;
                    toast.error(`Cannot save yet — ${n} field${n === 1 ? "" : "s"} need attention.`, { duration: 4000 });
                  }
                }}
                data-testid="save-message-wrap">
                <Button onClick={saveMessage} disabled={saving || messageValidationErrors.length > 0}
                  title={messageValidationErrors.length > 0 ? `${messageValidationErrors.length} field${messageValidationErrors.length === 1 ? "" : "s"} need attention before saving` : undefined}
                  className={`w-full bg-blue-600 hover:bg-blue-500 text-white h-10${messageValidationErrors.length > 0 ? " pointer-events-none" : ""}`}
                  data-testid="save-message-btn">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</> : <><Mail className="w-4 h-4 mr-2" />Send Telephone Message</>}
                </Button>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
    {/* Contact Activity Panel */}
    {contactId && (
      <ContactActivityPanel
        contactId={contactId}
        open={showActivityPanel}
        onClose={() => setShowActivityPanel(false)}
        onNavigateToFull={(id) => { setShowActivityPanel(false); onClose(); window.location.href = `/contacts/${id}`; }}
      />
    )}
  </>
  );
}
